import { useSyncExternalStore } from 'react';

const COUNTDOWN_MINUTES = 10;
const STORAGE_KEY = 'zumba-launch-deadline';

let deadline: number | null = null;
let remaining = 0;
let timer: number | undefined;
const listeners = new Set<() => void>();

/**
 * The launch window lasts 10 minutes per visitor and is stored locally, so
 * refreshing or navigating back does not restart it.
 */
function readDeadline(): number {
  const fallback = Date.now() + COUNTDOWN_MINUTES * 60 * 1000;
  try {
    const parsed = Number(window.localStorage.getItem(STORAGE_KEY) ?? Number.NaN);
    if (Number.isFinite(parsed)) return parsed;
    window.localStorage.setItem(STORAGE_KEY, String(fallback));
  } catch {
    /* Storage unavailable (private mode): fall back to an in-memory deadline. */
  }
  return fallback;
}

function getRemaining(): number {
  if (deadline === null) {
    deadline = readDeadline();
    remaining = Math.max(0, deadline - Date.now());
  }
  return remaining;
}

function stopTimer() {
  if (timer === undefined) return;
  window.clearTimeout(timer);
  timer = undefined;
}

/** One shared clock for the whole page, aligned to the wall-clock second. */
function tick() {
  stopTimer();
  getRemaining();
  remaining = Math.max(0, (deadline as number) - Date.now());
  listeners.forEach((listener) => listener());
  if (remaining > 0 && !document.hidden) {
    timer = window.setTimeout(tick, 1000 - (Date.now() % 1000));
  }
}

function handleVisibility() {
  if (document.hidden) stopTimer();
  else tick();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) {
    document.addEventListener('visibilitychange', handleVisibility);
    tick();
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
    }
  };
}

const getSecondsLeft = () => Math.floor(getRemaining() / 1000);
const getIsExpired = () => getRemaining() <= 0;

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/** Re-renders every second: use only in the small component that shows the digits. */
export function useLaunchCountdown() {
  const totalSeconds = useSyncExternalStore(subscribe, getSecondsLeft);
  return {
    hours: pad(Math.floor(totalSeconds / 3600)),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
  };
}

/** Re-renders only once, when the launch window closes. */
export function useLaunchExpired(): boolean {
  return useSyncExternalStore(subscribe, getIsExpired);
}
